/**
 * What can be built, as the server describes it.
 *
 * **No numbers live here.** The catalogue is one file on the server —
 * `buildings/catalogue.json` — and this is the shape it arrives in, fetched at
 * startup exactly like the biome legend. A copy of the prices or the site rules
 * on this side would be a second source of truth, and the day the two disagree
 * is the day a player is shown a price the server refuses.
 *
 * This is the same arrangement as `Movement.ts` with one difference that is the
 * whole point: movement has twin *values* on both sides and that is a debt this
 * project writes down. Here the values cross the wire and only the *reading* of
 * them is written twice — a closed vocabulary of four conditions, so a new
 * building costs nothing on this side and only a new kind of condition does.
 */

import { Material } from './Materials';
import type { Placed } from './Placed';

/**
 * How many of a kind one player may hold.
 *
 * `belowLevel` is what makes a camp a camp: one at a time, counting only those
 * still below that rung — so a camp raised to a village stops counting and the
 * next one can be founded.
 */
export type Limit =
  | { kind: 'perPlayer'; most: number; belowLevel?: number }
  | { kind: 'unlimited' };

/**
 * What has to be true before a building can be raised a rung.
 *
 * A closed vocabulary, like the site conditions. **Two of these cannot be
 * checked yet** — skills and world eras — and the server refuses them outright
 * rather than passing them, so nothing here has to pretend either.
 */
export type Prerequisite =
  | { kind: 'goods'; material: string; amount: number }
  /**
   * Tylu mieszkańców w tej osadzie. **Warunek, nie koszt** — sprawdzany
   * i niezużywany, więc nie ma go czym „dosypać z sakwy" i nie liczy się do
   * tego, czego brakuje w magazynie.
   */
  | { kind: 'people'; amount: number }
  | { kind: 'skill'; skill: string; level: number }
  | { kind: 'era'; era: string };

/** What the next one costs, as a curve over how many are already owned. */
export type Cost =
  | { curve: 'flat'; material: Material; amount: number }
  | { curve: 'geometric'; material: Material; amount: number; growth: number; free: number }
  | { curve: 'power'; material: Material; amount: number; exponent: number; free: number };

/**
 * A condition about the ground itself. All of them hold, or it does not go up.
 *
 * What is *on* a cell is said by the four lists instead, in the vocabulary of
 * the object and biome dictionaries. Water is deliberately not a biome test —
 * a biome is a picture, the lake layer is a fact.
 */
export type Requirement =
  | { kind: 'land' }
  | { kind: 'reachable' }
  | { kind: 'flat'; radius: number; maxDrop: number }
  /** At least one of the six neighbours is water — sea or lake, read as a fact. */
  | { kind: 'shore' }
  /**
   * At least one of the six neighbours does **not** carry this object.
   *
   * The one thing the four lists cannot say: they ask whether some neighbour
   * matches, or whether none does, never whether some neighbour fails to.
   * Standing on the rim of a deposit is the second question.
   */
  | { kind: 'edge'; object: string }
  /**
   * At least one of the six neighbours is one of these buildings, on that rung
   * or higher, and belongs to whoever is building.
   *
   * The only condition that reads a neighbour's **level** and its **owner**
   * rather than what it is — a village is a camp on its second rung and wears
   * the same object as the camp it grew from. `any` is an *or*, which the rest
   * of `site` is not: a field goes up beside a village or beside another of
   * your own fields, and that is one rule rather than two.
   */
  | { kind: 'besideOwn'; any: { building: string; level: number }[] };

export interface Richness {
  /** Which object's thickness is worth something here — a wood for a lumberjack. */
  object: string;
  radius: number;
  /** Added per level of the richest cell in reach. */
  perLevel: number;
}

/**
 * One multiplier and the cell it applies to.
 *
 * The same vocabulary as the four site lists — `on` needs at least one match,
 * `notOn` allows none — so „łąka" comes out as the temperate climate without
 * woodland on it, which is what a meadow is here.
 */
export interface Ground {
  on?: string[];
  notOn?: string[];
  times: number;
}

/** Jeden surowiec i ile go wypada na sztukę produkcji. */
export interface Yield {
  material: Material;
  amount: number;
}

export interface Output {
  /**
   * Co jedna sztuka produkcji przynosi — **lista, bo zdobycz bywa
   * niepodzielna**. Chata myśliwych przynosi skórę i dwa mięsa z jednej
   * wyprawy: jedna stawka, jeden znacznik po tamtej stronie, dwa wiersze tutaj.
   *
   * Jednoelementowa u wszystkiego, co robi jeden surowiec.
   */
  yields: Yield[];
  /** Sztuk zdobyczy na godzinę, nie sztuk surowca. */
  perHour: number;
  /**
   * Ile **sztuk zdobyczy** zmieści, zanim zacznie marnować: pułap na surowiec
   * to ta liczba razy jego `amount`.
   */
  capacity: number;
  richness?: Richness;
  /**
   * What the cell underneath is worth, as multipliers on the rate.
   *
   * **Declared and not used, on purpose.** The rate a player sees comes off the
   * wire already multiplied — the server is the one place where the catalogue
   * and the map meet, and a copy of this arithmetic here would be the twin-rule
   * debt this project writes down for movement, taken on for a number that
   * travels perfectly well. It is in this file so that a rename on the other
   * side is caught by the test that compares the two.
   */
  ground?: Ground[];
}

export interface Level {
  seconds: number;
  /** What this rung is called, where it is a different thing rather than a bigger one. */
  label?: string;
  /**
   * Który plik rysuje **ten** szczebel, gdy różni się od poprzedniego.
   *
   * Plik należy do szczebla, nie do rodzaju: obóz podniesiony o szczebel
   * **jest wioską** — ten sam wpis katalogu, inna rzecz. Brak znaczy „ten sam,
   * co rodzaju"; tak stoją drwal i kamieniołom, którym awans nie zmienia dziś
   * wyglądu.
   */
  model?: string;
  /** Everything that has to hold before this rung can be reached. */
  requires?: Prerequisite[];
  output?: Output;
  /**
   * Czy budynek na tym szczeblu jest **osadą**, czyli ma własną nazwę.
   *
   * Czytane, a nie zgadywane: klient nie rozpoznaje osady po kluczu budynku ani
   * po numerze poziomu — pyta katalogu, tak jak o wszystko inne.
   */
  named?: boolean;
  /**
   * Ilu graczy może pod tym dachem naraz odpoczywać. Zero znaczy „to nie jest
   * dach", i tak samo czyta się brak pola.
   *
   * Czytane z katalogu, a nie zgadywane po rodzaju budynku: to jest liczba
   * szczebla i wyższe szczeble będą ją miały większą.
   */
  beds?: number;
  /**
   * Ile statków zmieści się przy tym budynku naraz — i **czy w ogóle umie je
   * budować**.
   *
   * Zero, czyli brak pola, znaczy „to nie jest stocznia". Nie ma osobnej flagi
   * „buduje statki": byłaby drugim zapisem tej samej rzeczy i dałaby się
   * ustawić niezgodnie z tą liczbą. Czytane, a nie zgadywane po kluczu budynku.
   */
  berths?: number;
}

export interface BuildingKind {
  /** The wire format. Append, never reorder. */
  id: number;
  key: string;
  label: string;
  /** What a cell with this on it carries, in the object dictionary. */
  object: string;
  /**
   * Czy nazwę tego rodzaju czyta się **z tytułem szczebla**.
   *
   * „Warownia Czarnogród", „Zamek Czarnogród", „Twierdza Czarnogród" — a wioska
   * zostaje Czarnogrodem. Własność rodzaju, choć `named` jest własnością
   * szczebla: to, **czy** szczebel ma nazwę, zmienia się między szczeblami
   * (obóz nie ma, wioska ma), a to, **jak** się ją czyta, nie zmienia się
   * nigdy w obrębie rodzaju.
   */
  titled?: boolean;
  /**
   * Który plik z `public/models` rysuje ten rodzaj, bez rozszerzenia.
   *
   * Obowiązuje na każdym szczeblu, którego własny nie przesłania; brak znaczy
   * „ten rodzaj nie ma jeszcze pliku" i zostawia go przy zastępczej sylwetce
   * z `BuildingShapes.ts`. **Przyjeżdża z serwera jak wszystko inne tutaj** —
   * lista nazw plików po tej stronie była kopią opisu, który katalog i tak
   * niesie, i rozjeżdżała się przy każdym modelu dołożonym w panelu.
   */
  model?: string;
  /** What may be on the cell: empty means anything, otherwise at least one. */
  allowedField?: string[];
  /** What may not be on it: the cell must match none. */
  disallowedField?: string[];
  /** At least one of the six neighbours has to match one of these. */
  allowedNeighbor?: string[];
  /** No neighbour may match any of these. */
  disallowedNeighbor?: string[];
  /**
   * Ile pustki wokół siebie ten budynek **wymaga i wymusza**, w heksach.
   *
   * Siedem znaczy: nie stanie bliżej niż siedem pól od czegokolwiek, co stoi,
   * i nic nie stanie bliżej niż siedem pól od niego — także cudze. Brak pola
   * znaczy „nie zajmuję nikomu miejsca".
   */
  clearance?: number;
  /**
   * Ile pól budynek zajmuje **po każdej stronie osi**, poza swoim własnym.
   *
   * Stocznia ma tu jedynkę: hala na polu środkowym, doki na dwóch
   * przeciwległych sąsiadach. Brak pola znaczy budynek jednopolowy, czyli
   * wszystko poza nią — i dlatego dołożenie tego nie kosztowało po tej stronie
   * ani jednej reguły więcej: `footprint` zwraca wtedy jedno pole i reszta
   * czyta listę, nie przypadek szczególny.
   *
   * Którą osią, mówi `axis` na postawionym budynku (`Placed`): 0, 1 albo 2,
   * krawędź `axis` i przeciwległa `axis + 3` — ta sama konwencja, co przy
   * moście.
   */
  wings?: number;
  limit: Limit;
  /** Whether goods may be put into it — a camp does, a lumberjack's hut does not. */
  accepts?: boolean;
  /** Whether goods may be taken out of it. */
  dispenses?: boolean;
  /** Absent means free however many are owned. */
  cost?: Cost;
  site: Requirement[];
  levels: Level[];
}

/**
 * Co kosztuje droga, ile trwa i ile jest warta.
 *
 * **Wszystko na krawędź, nie na pole.** Pole, przez które droga przechodzi, ma
 * dwie krawędzie, a koniec jedną — prosty odcinek z dziesięciu pól to jedenaście
 * krawędzi. Dołączenie do drogi, która już tam jest, kosztuje tylko te
 * krawędzie, których nie było.
 */
export interface RoadRecipe {
  material: Material;
  perEdge: number;
  secondsPerEdge: number;
  /**
   * Ile szybciej idzie się polem z gotową drogą.
   *
   * **Deklarowane, nieużywane po tej stronie** — tak jak mnożniki podłoża:
   * o czas kroku pyta się serwera, a druga kopia tej arytmetyki tutaj byłaby
   * bliźniaczą regułą wziętą na siebie bez powodu. Jest tu, żeby przemianowanie
   * pola po tamtej stronie złapał test porównujący obie deklaracje.
   */
  speed: number;
  maxCells: number;
  /** Most przez jedno pole wody: cena w surowcu drogi i czas budowy. */
  bridge: { cost: number; seconds: number };
}

/** Wozy: jak wolno jadą wobec kroku człowieka. */
export interface HaulageRecipe {
  /**
   * Tempo wobec kroku człowieka; 0,5 to dwa razy dłużej na polu.
   *
   * **Deklarowane, nieużywane po tej stronie** — o czas kroku pyta się serwera,
   * a wóz i tak rysuje się tam, gdzie serwer powiedział, że jest.
   */
  pace: number;
}

/**
 * Jak ten budynek się nazywa — jedno miejsce na całą tę regułę.
 *
 * Trzy odpowiedzi, w tej kolejności:
 *
 * - **rodzaj z tytułem**, gdy ma nazwę i `titled`: „Warownia Czarnogród".
 *   Tytuł bierze się ze szczebla, więc ta sama warownia podniesiona czyta się
 *   „Zamek Czarnogród" — bez zmiany czegokolwiek w bazie, bo zmienił się
 *   szczebel, a nie nazwa;
 * - **sama nazwa**, gdy ją ma: Białogród jest wioską, ale nazywa się Białogród,
 *   a nagłówek „wioska" nad jego panelem byłby opisem katalogu zamiast nazwy
 *   miejsca;
 * - **nazwa rodzaju**, gdy nazwy nie ma. Rodzaj, o którym ten build nie słyszał,
 *   dostaje swój numer — budynek stoi tam niezależnie od tego, czy klient wie,
 *   czym jest.
 *
 * **Jedna funkcja, bo pytają o to cztery miejsca naraz**: dymek, menu pod
 * prawym przyciskiem, panel pola i lista celów wysyłki. Rozpisana w każdym
 * z osobna rozjechałaby się przy pierwszej zmianie — i rozjechała już raz,
 * zanim powstała.
 */
export function nameOf(kind: BuildingKind | undefined, placed: Placed): string {
  if (!placed.name) return kind?.label ?? `budynek ${placed.kind}`;
  if (!kind?.titled) return placed.name;

  // Szczebel mówi swoją nazwą, a rodzaj jest odwrotem: pierwszy szczebel
  // warowni własnego `label` nie ma, bo jest po prostu warownią.
  const rung = kind.levels[placed.level - 1]?.label ?? kind.label;
  return `${rung.charAt(0).toUpperCase()}${rung.slice(1)} ${placed.name}`;
}

/**
 * Z czego i jak długo powstaje statek.
 *
 * **Obok dróg i wozów, a nie w stoczni**, bo statek nie jest ani szczeblem
 * stoczni, ani jej produkcją: stocznia mówi tylko, ile ich naraz zmieści
 * (`berths` przy szczeblu), a czym jest statek — ten blok.
 */
export interface ShipRecipe {
  /** Co schodzi z magazynu stoczni — lista, bo kadłub i żagle to dwa surowce. */
  cost: { material: Material; amount: number }[];
  seconds: number;
  /**
   * Ile razy szybciej statek przechodzi pole niż człowiek.
   *
   * **Deklarowane, a liczone z bliźniaczej stałej** (`SAIL_PACE` w `Movement.ts`
   * i w `ShipMarkers.ts`) — ten sam dług, co przy `roads.speed`: podgląd trasy
   * musi mierzyć czas tak, jak zmierzy go serwer. Pole jest tutaj po to, żeby
   * przemianowanie go po tamtej stronie złapał test porównujący obie strony.
   */
  pace: number;
}

export interface Catalogue {
  version: number;
  buildings: BuildingKind[];
  roads: RoadRecipe;
  haulage: HaulageRecipe;
  ships: ShipRecipe;
}

/**
 * The shape this file knows how to read.
 *
 * Checked rather than assumed: a catalogue whose shape has moved on is not
 * something to interpret optimistically — the fields it is missing are the ones
 * a price or a condition would silently come out wrong from.
 */
export const CATALOGUE_VERSION = 24;

export async function loadCatalogue(api: string): Promise<Catalogue> {
  const response = await fetch(`${api}/api/world/buildings`);
  if (!response.ok) {
    throw new Error(`katalog budynków: ${response.status}`);
  }

  const catalogue = (await response.json()) as Catalogue;
  if (catalogue.version !== CATALOGUE_VERSION) {
    throw new Error(
      `katalog budynków w wersji ${catalogue.version}, klient czyta ${CATALOGUE_VERSION}`
    );
  }
  return catalogue;
}

/**
 * Czego brakuje do tego szczebla, albo `undefined`.
 *
 * **Bliźniak `Prerequisite::refusal` z serwera**, i jest tu z tego samego
 * powodu, dla którego jest tu `priceOf`: warunek musi wyszarzyć przycisk,
 * zanim ktokolwiek w niego kliknie. Serwer i tak sprawdza wszystko od nowa
 * i jego odpowiedź jest tą, która obowiązuje.
 *
 * Rodzaje, których jeszcze nie da się sprawdzić, **odmawiają** — tak samo jak
 * tam. Regułą, która wygląda na obowiązującą i jest po cichu pomijana, nie
 * chcemy się różnić między stronami.
 */
export function unmet(
  level: Level | undefined,
  held: (material: string) => number,
  named: (material: string, amount: number) => string,
  people = 0,
  godmode = false
): string | undefined {
  for (const need of level?.requires ?? []) {
    if (need.kind === 'people') {
      // Wymóg ludności **znika** w trybie testowym: nie jest surowcem, więc
      // „kosztuje jeden" go nie dotyczy, a zostawiony w mocy blokowałby
      // dokładnie to, do czego ten tryb najbardziej służy.
      if (!godmode && people < need.amount) {
        return `za mało mieszkańców: ${people} z ${need.amount}`;
      }
    } else if (need.kind === 'goods') {
      const wanted = charged(need.amount, godmode);
      const have = held(need.material);
      if (have < wanted) {
        // The server's own words, to the letter. This answer is only seen when
        // the two sides disagree, and disagreeing in wording as well would make
        // that look like a different rule — the same reason the placement
        // refusals are spelled identically on both sides.
        return `w magazynie budynku brakuje: ${named(need.material, wanted)} ${have} z ${wanted}`;
      }
    } else if (need.kind === 'skill') {
      return 'wymaga umiejętności — to jeszcze nie działa';
    } else {
      return 'wymaga epoki świata — to jeszcze nie działa';
    }
  }
  return undefined;
}

/** Ile brakuje w magazynie do tego szczebla, surowiec po surowcu. */
export function missingFor(
  level: Level | undefined,
  held: (material: string) => number,
  godmode = false
): { material: string; amount: number }[] {
  const short: { material: string; amount: number }[] = [];
  for (const need of level?.requires ?? []) {
    // Wyłącznie towary: ludzi nie da się przynieść w sakwie, więc „brakuje
    // dwustu mieszkańców" nie jest brakiem, który da się czymkolwiek uzupełnić.
    if (need.kind !== 'goods') continue;
    const gap = charged(need.amount, godmode) - held(need.material);
    if (gap > 0) short.push({ material: need.material, amount: gap });
  }
  return short;
}

/** The entry for a key, or undefined — the caller decides what that means. */
export function buildingOf(catalogue: Catalogue, key: string): BuildingKind | undefined {
  return catalogue.buildings.find((kind) => kind.key === key);
}

/**
 * The entry for an id.
 *
 * What a placed building carries is the id, because that is the wire format —
 * the same reason a cell carries a biome id rather than its name.
 */
export function buildingById(catalogue: Catalogue, id: number): BuildingKind | undefined {
  return catalogue.buildings.find((kind) => kind.id === id);
}

/**
 * What the next one costs, given how many are already owned.
 *
 * **Twin of `Cost::price` in `mod.rs`** — and the only arithmetic on this side,
 * because a price has to be on a button before anybody clicks it. The curve and
 * its numbers still come from the server; what is repeated is how to read them.
 */
/**
 * Tryb testowy: co kosztuje **jedną sztukę** i trwa pięć sekund.
 *
 * **Bliźniak `AppState::charges` i `eased` z serwera.** Ta strona rysuje ceny
 * i wyszarzenia sama, więc musi znać tę samą regułę — a jedyną rzeczą, którą tu
 * trzeba wiedzieć, jest to, czy tryb trwa. Flaga przychodzi w powitaniu
 * i rozgłasza się przy zmianie, jak niebo.
 *
 * Funkcja zamiast rozsypanych `if`-ów po to, żeby w dniu, w którym ułatwienie
 * zmieni kształt, było jedno miejsce po tej stronie drutu — tak jak po tamtej.
 */
export function charged(amount: number, godmode: boolean): number {
  return godmode ? Math.min(amount, 1) : amount;
}

export function priceOf(cost: Cost | undefined, owned: number): number {
  if (!cost) return 0;

  switch (cost.curve) {
    case 'flat':
      return Math.round(cost.amount);
    case 'geometric':
      return owned < cost.free ? 0 : Math.round(cost.amount * cost.growth ** (owned - cost.free));
    case 'power':
      return owned < cost.free ? 0 : Math.round(cost.amount * (owned - cost.free + 1) ** cost.exponent);
  }
}
